<!-- ══════════════════════════════════════════════
     Page : Édition d'un type d'activité
     ⚠️  TS : ajouter dans la classe →  public routeAlias = routeAlias;
              et dans imports →  RouterLink
     ══════════════════════════════════════════════ -->
<div class="add-page">

  <!-- ── Page Header ── -->
  <header class="add-page__header">
    <div class="add-page__header-left">
      <a
        class="add-page__back"
        [routerLink]="['/', routeAlias.parameters.home, routeAlias.parameters.activity.home, routeAlias.parameters.activity.type_activite.home]"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Retour
      </a>
      <h1 class="add-page__title">Modifier un type d'activité</h1>
      <p class="add-page__desc">Modifiez le nom et les tâches du type d'activité sélectionné.</p>
    </div>

    <!-- Stepper visuel -->
    <ol class="add-stepper">
      <li class="add-stepper__step" [class.add-stepper__step--done]="currentTypeActiviteData?.type_activite">
        <span class="add-stepper__badge">1</span>
        <span class="add-stepper__label">Nom</span>
      </li>
      <span class="add-stepper__sep"></span>
      <li class="add-stepper__step" [class.add-stepper__step--active]="currentTypeActiviteData?.type_activite">
        <span class="add-stepper__badge">2</span>
        <span class="add-stepper__label">Tâches</span>
      </li>
      <span class="add-stepper__sep"></span>
      <li class="add-stepper__step" [class.add-stepper__step--active]="currentTypeActiviteData?.taches?.length">
        <span class="add-stepper__badge">3</span>
        <span class="add-stepper__label">Enregistrer</span>
      </li>
    </ol>
  </header>

  <div class="add-page__body">

    <!-- ══ STEP 1 : Nom du type d'activité ══ -->
    <section class="add-card">
      <div class="add-card__header">
        <span class="add-card__step-badge">1</span>
        <div>
          <h2 class="add-card__title">Nom du type d'activité</h2>
          <p class="add-card__subtitle">Modifiez le nom de ce type d'activité.</p>
        </div>
      </div>

      <form [formGroup]="typeActiviteForm" (ngSubmit)="onSubmitTypeActivite()" class="add-name-form">
        <div class="add-form__field">
          <app-input
            formControlName="nomTypeActivite"
            type="text"
            label="Nom du type d'activité"
            placeholder="Ex : Activité de planification"
          />
          @if (typeActiviteForm.get('nomTypeActivite')?.invalid && typeActiviteForm.get('nomTypeActivite')?.touched) {
            <span class="add-form__error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Le nom du type d'activité est requis.
            </span>
          }
        </div>

        <div class="add-form__footer">
          <app-ui-button
            label="Annuler"
            variant="danger"
            size="md"
            iconLeft="close"
            type="button"
            [routerLink]="['/', routeAlias.parameters.home, routeAlias.parameters.activity.home, routeAlias.parameters.activity.type_activite.home]"
          ></app-ui-button>
          <app-ui-button
            label="Valider le nom"
            variant="primary"
            size="md"
            iconRight="check"
            type="submit"
            [disabled]="typeActiviteForm.invalid"
            [loading]="isLoading"
          ></app-ui-button>
        </div>
      </form>
    </section>

    <!-- ══ STEP 2 : Tâches ══ -->
    @if (currentTypeActiviteData && currentTypeActiviteData.type_activite) {
      <section class="add-card add-card--tasks">

        <div class="add-card__header">
          <span class="add-card__step-badge add-card__step-badge--accent">2</span>
          <div>
            <h2 class="add-card__title">Tâches — {{ currentTypeActiviteData.type_activite }}</h2>
            <p class="add-card__subtitle">Modifiez, ajoutez ou supprimez des tâches (total ≤ 100 %).</p>
          </div>
        </div>

        <!-- ── Formulaire d'ajout de tâche ── -->
        <div class="add-task-panel">
          <p class="add-task-panel__title">Ajouter une tâche</p>

          @if (taskForm.get('tache')?.invalid && (taskForm.get('tache')?.dirty || taskForm.get('tache')?.touched)) {
            <div class="add-alert add-alert--error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Le nom de la tâche est requis.
            </div>
          }
          @if (taskForm.get('poids')?.invalid && (taskForm.get('poids')?.dirty || taskForm.get('poids')?.touched)) {
            <div class="add-alert add-alert--error">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              @if (taskForm.get('poids')?.errors?.['required']) { Le poids est requis. }
              @else if (taskForm.get('poids')?.errors?.['pattern']) { Le poids doit être un nombre entier. }
            </div>
          }

          <form [formGroup]="taskForm" (ngSubmit)="onAddTask()" class="add-task-form">
            <div class="add-task-form__inputs">
              <div class="add-task-form__tache">
                <app-input
                  label="Nom de la tâche"
                  placeholder="Ex : Rédiger le rapport"
                  formControlName="tache"
                  type="text"
                />
              </div>
              <div class="add-task-form__poids">
                <app-input
                  label="Poids (%)"
                  placeholder="Ex : 30"
                  formControlName="poids"
                  type="text"
                />
              </div>
              <div class="add-task-form__action">
                <app-ui-button
                  label="Ajouter"
                  variant="secondary"
                  size="md"
                  iconLeft="add"
                  type="submit"
                  [disabled]="taskForm.invalid"
                ></app-ui-button>
              </div>
            </div>
          </form>
        </div>

        <!-- ── Tableau des tâches ── -->
        <div class="add-tasks-list">
          <div class="add-tasks-list__header">
            <h3 class="add-tasks-list__title">
              Liste des tâches
              <span class="add-tasks-list__badge">{{ currentTypeActiviteData.taches?.length || 0 }}</span>
            </h3>
          </div>

          @if (!currentTypeActiviteData.taches?.length) {
            <div class="add-empty-state">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1"/>
                <line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>
              </svg>
              <p>Aucune tâche pour l'instant. Utilisez le formulaire ci-dessus pour en ajouter.</p>
            </div>
          } @else {
            <div class="add-table-wrapper">
              <app-ui-table
                [columns]="tableTache"
                [data]="currentTypeActiviteData.taches"
                [actions]="rowActions"
              />
            </div>
          }
        </div>

        <!-- ══ STEP 3 : Save bar ══ -->
        <div class="add-save-bar">
          <div class="add-save-bar__info">
            <span class="add-card__step-badge">3</span>
            <div>
              <p class="add-save-bar__title">Enregistrer les modifications</p>
              <p class="add-save-bar__hint">
                {{ currentTypeActiviteData?.taches?.length || 0 }}
                tâche{{ (currentTypeActiviteData?.taches?.length || 0) > 1 ? 's' : '' }} configurée{{ (currentTypeActiviteData?.taches?.length || 0) > 1 ? 's' : '' }}
              </p>
            </div>
          </div>

          <app-ui-button
            label="Modifier tout"
            variant="primary"
            size="md"
            iconLeft="save"
            type="button"
            [loading]="isLoading"
            [disabled]="isLoading || !currentTypeActiviteData?.taches?.length"
            (clicked)="onSubmit()"
          ></app-ui-button>
        </div>

      </section>
    }

  </div><!-- /.add-page__body -->

  <!-- ── Template : Actions de ligne ── -->
  <ng-template #rowActions let-item="item">
    <div class="row-actions">
      <app-ui-button
        label="Modifier"
        variant="ghost"
        size="sm"
        iconLeft="edit"
        type="button"
        (clicked)="onEditTask(item)"
      ></app-ui-button>

      <app-ui-button
        iconOnly="delete"
        variant="danger"
        size="sm"
        ariaLabel="Supprimer la tâche"
        type="button"
        (clicked)="onDeleteTask(item)"
      ></app-ui-button>
    </div>
  </ng-template>

</div><!-- /.add-page -->


<!-- ══ Modal : Édition d'une tâche ══ -->
<app-modal [isVisible]="isModalVisible" title="Modifier la tâche" (closeEvent)="onCloseModal()">
  <form [formGroup]="editTaskForm" (ngSubmit)="onSaveEditedTask()" class="add-form">

    <div class="add-form__field">
      <app-input label="Nom de la tâche" formControlName="tache" type="text" />
      @if (editTaskForm.get('tache')?.invalid && (editTaskForm.get('tache')?.dirty || editTaskForm.get('tache')?.touched)) {
        <span class="add-form__error">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Le nom de la tâche est requis.
        </span>
      }
    </div>

    <div class="add-form__field">
      <app-input label="Poids (%)" formControlName="poids" type="text" />
      @if (editTaskForm.get('poids')?.invalid && (editTaskForm.get('poids')?.dirty || editTaskForm.get('poids')?.touched)) {
        <span class="add-form__error">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          @if (editTaskForm.get('poids')?.errors?.['required']) { Le poids est requis. }
          @else if (editTaskForm.get('poids')?.errors?.['pattern']) { Le poids doit être un nombre entier. }
        </span>
      }
    </div>

    <div class="add-form__modal-actions">
      <app-ui-button
        label="Annuler"
        variant="ghost"
        size="md"
        type="button"
        (clicked)="onCloseModal()"
      ></app-ui-button>
      <app-ui-button
        label="Sauvegarder"
        variant="primary"
        size="md"
        iconLeft="save"
        type="submit"
        [disabled]="editTaskForm.invalid"
      ></app-ui-button>
    </div>

  </form>
</app-modal>